import {
  authHeadersForJohkuRequest,
  headersToPlainObject,
  isJohkuBackendRequest,
  resolveJohkuRequestUrl
} from '~/lib/johkuApiAuth'
import {
  beginSessionRecovery,
  tryRefreshBetterAuthSession
} from '~/lib/sessionRecovery'

type FetchOptionsWithAuth = {
  skipAuthRecovery?: boolean
  _authRetried?: boolean
  headers?: HeadersInit
  baseURL?: string
  retry?: number
  retryStatusCodes?: number[]
}

export default defineNuxtPlugin((nuxtApp) => {
  const runtimeConfig = useRuntimeConfig()
  const baseDomain = runtimeConfig.public.baseDomain as string
  const serviceDomain = runtimeConfig.public.serviceDomain as string
  const pluginStore = nuxtApp.$store as {
    state: { user?: unknown, sessionNeedsRecovery?: boolean }
    getters?: { apiKey?: string | null }
    dispatch: (action: string, payload?: unknown) => unknown
  }

  function getStore () {
    try {
      return (useNuxtApp().$store as typeof pluginStore) || pluginStore
    } catch {
      return pluginStore
    }
  }

  globalThis.$fetch = $fetch.create({
    onRequest ({ request, options }) {
      const url = typeof request === 'string' ? request : request.url
      if (!isJohkuBackendRequest(url, baseDomain, serviceDomain)) {
        return
      }
      const apiKey = getStore()?.getters?.apiKey
      options.headers = authHeadersForJohkuRequest(apiKey, options.headers as Record<string, string> | Headers)
    },
    async onResponseError ({ request, response, options }) {
      const fetchOptions = options as FetchOptionsWithAuth
      if (fetchOptions.skipAuthRecovery || fetchOptions._authRetried) {
        return
      }
      if (response.status !== 401 && response.status !== 403) {
        return
      }
      const url = typeof request === 'string' ? request : request.url
      const requestUrl = resolveJohkuRequestUrl(url, fetchOptions.baseURL)
      if (!isJohkuBackendRequest(requestUrl, baseDomain, serviceDomain)) {
        return
      }
      const store = getStore()
      const refreshed = await tryRefreshBetterAuthSession(store)
      if (refreshed.status === 'ok') {
        fetchOptions._authRetried = true
        const headers = headersToPlainObject(fetchOptions.headers as Record<string, string> | Headers)
        headers.Authorization = 'Bearer ' + refreshed.token
        fetchOptions.headers = headers as HeadersInit
        // ofetch retries after onResponseError when retry > 0 and the status
        // is listed. Default retry codes do not include 401/403.
        // A second 401/403 after a confirmed session is a real request error,
        // not a dead session — _authRetried prevents another overlay.
        fetchOptions.retry = 1
        fetchOptions.retryStatusCodes = [401, 403]
        return
      }
      if (refreshed.status === 'expired') {
        beginSessionRecovery(store)
      }
    }
  }) as typeof globalThis.$fetch
})
